import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
  Card,
  CardActionArea,
  CardContent
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { collection, query, onSnapshot, Timestamp } from 'firebase/firestore';
import { store } from '../../config/firebase';
import { useNavigate } from 'react-router-dom';
import { useColorMode } from '../../hooks/colorModeProvider';
import { GameStatus } from '../../constants/gameConstants';

const PublicScoringEventsList = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const { mode, setMode } = useColorMode();

  useEffect(() => {
    setMode('light');
  }, [mode, setMode]);

  useEffect(() => {
    const eventsRef = collection(store, 'events');
    const q = query(eventsRef);

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const eventsData = querySnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));

        // Sort by startDate descending (most recent first)
        const sortedEvents = eventsData.sort((a, b) => {
          const aDate = a.startDate?.toDate?.() || new Date(a.startDate);
          const bDate = b.startDate?.toDate?.() || new Date(b.startDate);
          aDate.setHours(0, 0, 0, 0);
          bDate.setHours(0, 0, 0, 0);
          return bDate - aDate;
        });

        // Filter out events with endDate older than today + 1
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrowStart = new Date(today);
        tomorrowStart.setDate(tomorrowStart.getDate());

        const filteredEvents = sortedEvents.filter((event) => {
          // Events with publishing switched off are excluded from the public
          // list. Absent means publish, so events saved before this setting
          // existed stay visible.
          if (event.publishEvent === false) return false;

          const endDate = event.endDate?.toDate?.() || new Date(event.endDate);
          endDate.setHours(23, 59, 59, 999);
          return endDate >= tomorrowStart;
        });

        setEvents(filteredEvents);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching events:', err);
        setError('Failed to load events');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleEventClick = (eventId) => {
    navigate(`/public-scoring/event/${eventId}`);
  };

  const isEventActive = (event) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startDate = event.startDate?.toDate?.() || new Date(event.startDate);
    const endDate = event.endDate?.toDate?.() || new Date(event.endDate);

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    return today >= startDate && today <= endDate;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading events...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Public Scoring Events
      </Typography>

      {events.length === 0 ? (
        <Typography variant="h6">No events available</Typography>
      ) : (
        <Stack alignItems="center" gap={2} sx={{ width: '100%', maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>
            {events.length} event{events.length !== 1 ? 's' : ''}
          </Typography>

          {events.map((event) => (
            <Card
              key={event.id}
              sx={{
                width: '100%',
                cursor: 'pointer',
                '&:hover': { boxShadow: 6 },
                border: isEventActive(event) ? '3px solid' : 'none',
                borderColor: isEventActive(event) ? 'info.main' : 'transparent'
              }}>
              <CardActionArea onClick={() => handleEventClick(event.id)}>
                <CardContent
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6" component="div">
                      {event.name || 'Unnamed Event'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      {event.startDate
                        ? (
                            event.startDate?.toDate?.() || new Date(event.startDate)
                          ).toLocaleDateString()
                        : 'Date unknown'}
                    </Typography>
                  </Box>
                  <ChevronRightIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default PublicScoringEventsList;
